import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { CheckboxWithLabel, TextField } from 'formik-material-ui';
import { Button, CircularProgress, InputLabel } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import * as yup from 'yup';
import has from 'lodash/has';
import 'react-phone-number-input/style.css';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';

import { trackAhoyEvent, updateAhoyToken } from 'utils/ahoy';
import { postRequest } from 'utils/api';
import { getTokenFromResponse, identifyFullStoryUser, setCrispUser } from 'utils/helpers';
import { signIn } from 'features/authSlice';
import { closeModal, openModal, setFooter, setRoleFilter } from 'features/utilsSlice';
import {
  getReferralCode,
  saveAccessToken,
  setLastSignInRole,
  setInventorySetup,
  getUuid,
  deleteUuid,
  getRedirectTo,
  setRedirectTo,
  setCurrentUserLS,
  setDiscoverTab,
} from 'utils/localStorage';
import User from 'public/icons/form/user.svg';
import Email from 'public/icons/form/email.svg';
import AnimateListItem from 'components/animations/AnimateListItem';
import AnimateList from 'components/animations/AnimateList';
import Switch from 'components/Switch';
import PasswordConfirmation from 'components/PasswordConfirmation';
import SwitchTabs from 'components/SwitchTabs';
import styles from 'styles/components/auth/AuthForm.module.scss';

const defaultRequired = 'is required.';
const width = { width: '100%' };

const schema = yup.object({
  contact_email: yup
    .string()
    .email('is not a valid email')
    .required('is required'),
  phone: yup.number().required(defaultRequired),
  profiles: yup.object({
    display_name: yup.string().required(defaultRequired),
    username: yup
      .string()
      .min(4, 'Must contain at least 4 characters')
      .matches(/^[a-zA-Z0-9_.-]*$/, 'is invalid (no spaces or special characters allowed).')
      .required(defaultRequired),
  }),
  notification_type: yup.string().required(defaultRequired),
  password: yup
    .string()
    .required(defaultRequired)
    .min(6, 'Must contain at least 6 characters'),
  password_confirmation: yup
    .string()
    .required(defaultRequired)
    .oneOf([yup.ref('password')], "Passwords don't match"),

  terms_of_service: yup
    .bool()
    .oneOf(
      [true],
      'You must agree to the Terms of Service to use our site. This includes our Privacy Policy, Cookie Policy, and Acceptable Use Policy.'
    ),
  notifications: yup
    .bool()
    .oneOf(
      [true],
      'Notifications are required for users on our site: notifications for new gigs, when to post, when to take down posts, when you’ve made money, when you can cash out, new gig requests, messages from buyers and sellers etc. This is required functionality for our site to work.'
    ),
});

const initialValues = {
  contact_email: '',
  phone: undefined,
  profiles: {
    display_name: '',
    username: '',
  },
  password: '',
  password_confirmation: '',
  notifications: false,
  notification_type: 'sms',
  terms_of_service: false,
};

const InfluencerForm = ({ sendToken = false, isDialog = false, primaryInterest = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { query } = router;

  const handleSubmit = ({ profiles, ...values }, actions) => {
    setIsLoading(true);
    const refCode = getReferralCode();
    const uuid = getUuid();
    const redirectTo = getRedirectTo();

    postRequest({
      endpoint: 'sign_up',
      data: {
        user: {
          profiles_attributes: [{ ...profiles, adult: type === 2 }],
          ...values,
          ref_code: refCode,
          role: 'influencer',
          primary_interest: primaryInterest ? primaryInterest : 'sell',
          notification_type:
            parsePhoneNumber(values.phone || '')?.country === 'US'
              ? values.notification_type
              : 'whatsapp',
        },
        uuid,
      },
      successMessage: (uuid && !isDialog) || !uuid ? 'Successfully signed up.' : '',
      version: null,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
        sendToken,
      },
      ...actions,
    })
      .then(response => {
        const token = getTokenFromResponse(response);

        if (token) saveAccessToken(token);

        if (response.data.primary_interest === 'buy') setDiscoverTab('influencers');

        dispatch(setRoleFilter(uuid ? 'advertiser' : 'influencer'));

        if (uuid) deleteUuid();

        if (uuid && isDialog) {
          dispatch(closeModal('Which Are You'));
          dispatch(openModal({ name: 'Full Screen' }));
        }

        dispatch(signIn(response.data));
        setCurrentUserLS(response.data);
        setCrispUser(response.data);
        identifyFullStoryUser(response.data);

        setLastSignInRole('influencer');
        setIsLoading(false);

        if (token) updateAhoyToken(token);
        trackAhoyEvent('Sign Up', { role: response.data.role, id: response.data.id });

        if (uuid && isDialog) return;

        if (uuid && !isDialog && redirectTo) {
          router.push(redirectTo);
          if (redirectTo) setRedirectTo(null);
        }

        router.push('/access-confirmation');
        setInventorySetup(true);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    if (router.pathname === '/sign-up/shoutout') {
      setType(2);
    }
  }, [router.pathname]);

  useEffect(() => {
    if (isMobile) {
      dispatch(setFooter(false));
    }

    return () => dispatch(setFooter(true));
  }, [dispatch, isMobile]);

  useEffect(() => {
    if ('m' in query) setType(2);
  }, [query]);

  return (
    <AnimateList>
      <Grid
        container
        className={clsx(styles.container, { [styles.paddingTop]: isDialog })}
        direction="column"
        alignItems="center"
      >
        {primaryInterest !== 'buy' && (
          <h1 style={isDialog ? { fontSize: 24 } : {}}>Influencer sign up</h1>
        )}

        <Grid style={width} item xs={11} sm={6} md={4} lg={3} xl={2}>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            noValidate
          >
            {({ isSubmiting, values, setFieldValue, handleBlur, errors }) => (
              <>
                {primaryInterest === 'buy' && (
                  <div className={styles.whoAreYou}>
                    <AnimateListItem>
                      <h3 style={{ textAlign: 'center' }}>Who are you?</h3>

                      <SwitchTabs
                        labels={['Athlete', 'Influencer', 'Model']}
                        value={type}
                        setValue={value => setType(value)}
                      />
                    </AnimateListItem>
                  </div>
                )}

                {(type !== false || (!type && router.pathname !== '/sign-up/advertiser')) && (
                  <Form className={styles.form}>
                    <AnimateListItem>
                      <span style={{ marginLeft: 14, opacity: 0.6, fontSize: 12 }}>
                        This will be the number you get notifications
                      </span>

                      <PhoneInput
                        name="phone"
                        placeholder="Phone Number"
                        onChange={phone => setFieldValue('phone', phone)}
                        onBlur={handleBlur}
                        defaultCountry="US"
                        defaultChecked={true}
                        className={clsx(styles.phoneInput, {
                          [styles.phoneInputError]: has(errors, 'phone', false),
                        })}
                      />

                      <ErrorMessage
                        name="phone"
                        component="span"
                        className={styles.error}
                        style={{ top: 60 }}
                      />
                    </AnimateListItem>

                    <AnimateListItem>
                      <Field
                        component={TextField}
                        name="profiles.display_name"
                        type="text"
                        value={values.profiles.display_name}
                        placeholder="Display Name (Name of your page)"
                        variant="outlined"
                        color="primary"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <User width={14} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </AnimateListItem>

                    <AnimateListItem>
                      <div style={{ position: 'relative' }}>
                        <Field
                          component={TextField}
                          name="profiles.username"
                          type="text"
                          value={values.profiles.username}
                          placeholder="Username"
                          variant="outlined"
                          color="primary"
                          className={errors['profiles.username'] ? styles.inputError : ''}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <User width={14} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {errors['profiles.username'] && (
                          <span className={styles.error}>{errors['profiles.username']}</span>
                        )}
                      </div>
                    </AnimateListItem>

                    <AnimateListItem>
                      <div style={{ marginTop: -10 }}>
                        <span style={{ marginLeft: 14, opacity: 0.6, fontSize: 12 }}>
                          Email to receive some notifications and receipts
                        </span>

                        <Field
                          component={TextField}
                          name="contact_email"
                          type="email"
                          placeholder="Email Address"
                          variant="outlined"
                          color="primary"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email width={14} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>
                    </AnimateListItem>

                    <PasswordConfirmation />

                    <AnimateListItem>
                      <div
                        style={{ position: 'relative' }}
                        className={errors.notifications && 'checkbox-nested-error'}
                      >
                        <Field
                          component={CheckboxWithLabel}
                          type="checkbox"
                          name="notifications"
                          Label={{
                            label: 'I agree to receive notifications to this phone',
                          }}
                          color="primary"
                        />

                        <ErrorMessage
                          name="notifications"
                          component="span"
                          className={styles.error}
                          style={{ top: -5, position: 'relative' }}
                        />
                      </div>
                    </AnimateListItem>

                    <AnimateListItem>
                      <div
                        style={{ position: 'relative' }}
                        className={errors.terms_of_service && 'checkbox-nested-error'}
                      >
                        <div style={{ width: '100%' }}>
                          <Field
                            component={CheckboxWithLabel}
                            type="checkbox"
                            name="terms_of_service"
                            Label={{ label: 'I agree to the Terms of Service' }}
                            color="primary"
                          />
                        </div>

                        <ErrorMessage
                          name="terms_of_service"
                          component="span"
                          className={styles.error}
                          style={{ top: -5, position: 'relative' }}
                        />
                      </div>
                    </AnimateListItem>

                    {parsePhoneNumber(values.phone || '')?.country === 'US' ? (
                      <AnimateListItem>
                        <div style={{ marginTop: 20, marginBottom: 20 }}>
                          <InputLabel>Which way of communication do you prefer?</InputLabel>

                          <Switch
                            firstOption={{ label: 'SMS', value: 'sms' }}
                            secondOption={{ label: 'WhatsApp', value: 'whatsapp' }}
                            setValue={value => setFieldValue('notification_type', value)}
                            value={values.notification_type}
                          />
                        </div>
                      </AnimateListItem>
                    ) : (
                      <div style={{ marginTop: 20, marginBottom: 20 }}>
                        We can only send notifications to WhatsApp in your country. Make sure to
                        download the app if you don't have it yet.
                      </div>
                    )}

                    <AnimateListItem>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isSubmiting || isLoading || !isEmpty(errors)}
                        type="submit"
                        fullWidth
                      >
                        {isLoading ? <CircularProgress color="primary" /> : 'Register'}
                      </Button>
                    </AnimateListItem>
                  </Form>
                )}
              </>
            )}
          </Formik>
        </Grid>
      </Grid>
    </AnimateList>
  );
};

export default InfluencerForm;
